// What a scanned tax bill was BILLED, whether it was PAID, and its early-payment DISCOUNT — the rules of
// docs/paid-bills-report.md, as one pure function the scanner calls at submit (src/pages/DocumentScanner.js persistBill).
// Unit-tested: src/__tests__/bill-payment.test.js.
//
//   billed      — the FULL tax, before any discount. A single-payment (annual) bill: ad valorem + non-ad valorem +
//                 penalty. An installment: that installment's printed amount (installment_amount), else its Amount Due
//                 when unpaid. Never the Amount Due of a paid bill (that is $0).
//   paid        — the document says so (paid_marker), or it prints $0 due while carrying tax. Catch-up years only.
//   paid date / amount — ONLY what the bill prints. Exception: a Florida bill paid with a discount → billed − discount.
//   discount (Florida):
//     • printed discount amount → kept as printed.
//     • unpaid, printed schedule → kept as discount_schedule; the discount is the full bill − the FIRST tier's printed
//       amount, by that tier's date. Marked derived (Philip, 2026-10-09: Orange County 2021, 03-24-30-0000-10004 —
//       $34,319.47 − the Nov 30 tier $32,946.69 = $1,372.78). ⛔ From the printed figures only, never by percent: a tier
//       printed without an amount derives nothing.
//     • paid, discount not printed, paid date known → the tier for the month paid: Nov 4%, Dec 3%, Jan 2%, Feb 1%,
//       later 0%. The discount date is the last day of that month. Marked derived. When the bill also prints that
//       month's tier, its amount gives the discount (full bill − tier); otherwise the percent of the full bill.
//       ⛔ THE TIER FOLLOWS THE EFFECTIVE DATE WHEN THE BILL PRINTS ONE. Florida grants the discount by the effective /
//       postmark date, not the day the payment posted. 30-3129-029-0010 / 2025 revised prints Effective Date 11/28/2025
//       and Paid 12/01/2025: by the paid date the tier was December 3% ($12,706.67); the client took November 4%
//       ($423,555.51 × 0.96 = $406,613.29 = $449,186.29 paid − the $42,573.00 refund). The paid date is the fallback.
//       Paid At stays the paid date — that is when the money posted.
//     • paid, no paid date → NO discount assumed, and the bill needs review.
//   Nothing ever applies a November tier without a paid date.
//
//   discountDecided — the scanner's form holds the discount (prefilled from this function, then editable). The
//   discount is then taken AS GIVEN (discountAvailable / discountAmount / discountDate / discountDerived = the
//   source it was derived from, '' once the TS edits it) and nothing is derived over it: a TS who clears the
//   discount has cleared it.

const round2 = (n) => Math.round(Number(n) * 100) / 100;
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const ymd = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const lastDay = (y, m) => new Date(y, m, 0).getDate();   // m is 1-based; day 0 of the NEXT month = last day of m (local)

// The Florida tier for a paid date ('YYYY-MM-DD') on a bill for taxYear. → { pct, date } — pct 0 means no discount.
// Bills go out in November of the tax year; anything paid by the end of November earns the November 4%.
export function floridaTierForPaidDate(paidDate, taxYear) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(paidDate || ''));
  const ty = Number(taxYear);
  if (!m || !Number.isFinite(ty)) return null;
  const y = Number(m[1]), mo = Number(m[2]);
  const at = y * 12 + mo;                      // months, comparable
  const tiers = [[ty, 11, 4], [ty, 12, 3], [ty + 1, 1, 2], [ty + 1, 2, 1]];
  for (const [ty2, mo2, pct] of tiers) {
    if (at <= ty2 * 12 + mo2) return { pct, date: ymd(ty2, mo2, lastDay(ty2, mo2)) };
  }
  return { pct: 0, date: null };
}

// Inputs (numbers already parsed; strings as printed):
//   state, taxYear, singlePayment (the collector schedules ONE installment that year), gross (adv + nav + penalty),
//   amountDue, printedTotal (the bill's grand total), installmentAmount, paidMarker ('Y'/'N'), paidDate, amountPaid,
//   effectiveDate (the payment's printed effective/postmark date, else null),
//   discountAmount, discountDate (printed), discountTiers ([{pct, pay_by, amount}] as printed), paidOverride (false = the
//   TS unticked "paid"; true = the TS ticked it; undefined = as detected), discountDecided / discountAvailable / discountDerived (above)
export function deriveBillPayment(i) {
  const fl = String(i.state || '').toUpperCase() === 'FL';
  const amountDue = num(i.amountDue);
  const gross = num(i.gross);
  const printedTotal = num(i.printedTotal);
  const carriesTax = (gross != null && gross > 0) || (printedTotal != null && printedTotal > 0);
  const detected = String(i.paidMarker || '').toUpperCase() === 'Y' || (amountDue === 0 && carriesTax);
  // paidOverride: false = the TS unticked it; true = the TS ticked "Already paid" on a bill that does not show it.
  const paid = i.paidOverride === false ? false : (i.paidOverride === true || detected);

  // A paid bill's printed Amount Due is $0, so it is no billed figure — but one marked paid by hand still prints its
  // real amount due, and that IS the installment's billed amount.
  const billed = i.singlePayment
    ? gross
    : (num(i.installmentAmount) ?? ((!paid || (amountDue != null && amountDue > 0)) ? amountDue : null));

  const tiers = (Array.isArray(i.discountTiers) ? i.discountTiers : [])
    .map((t) => ({ pct: num(t && t.pct), pay_by: (t && t.pay_by) || null, amount: num(t && t.amount) }))
    .filter((t) => t.pct != null && t.pct > 0);
  let discount = { available: null, amount: num(i.discountAmount), date: i.discountDate || null, derived: false };
  const review = [];

  if (fl && i.singlePayment && i.discountDecided) {
    const amt = discount.amount != null && discount.amount > 0 ? discount.amount : null;
    discount = { available: i.discountAvailable || (amt ? 'Yes' : null), amount: discount.amount, date: discount.date,
                 derived: !!i.discountDerived, ...(i.discountDerived ? { from: i.discountDerived } : {}) };
    if (paid && !amt && !(i.effectiveDate || i.paidDate)) review.push('FL paid bill with no paid date — discount unknown');
  } else if (fl && i.singlePayment) {
    if (discount.amount != null && discount.amount > 0) {
      discount.available = 'Yes';                                     // printed on the bill
    } else if (!paid && tiers.length) {
      const t1 = tiers[0];                                            // the first printed tier — what the TBT transmits
      const amt = (t1.amount != null && gross != null) ? round2(gross - t1.amount) : null;
      discount = amt != null && amt > 0
        ? { available: 'Yes', amount: amt, date: t1.pay_by, derived: true, from: 'tiers' }
        : { available: null, amount: null, date: null, derived: false };
    } else if (paid) {
      if (i.effectiveDate || i.paidDate) {
        const from = i.effectiveDate ? 'effective' : 'paid';
        const tier = floridaTierForPaidDate(i.effectiveDate || i.paidDate, i.taxYear);
        if (tier && tier.pct > 0 && gross != null) {
          const printed = tiers.find((t) => t.pct === tier.pct && t.amount != null && t.amount < gross);
          discount = { available: 'Yes', amount: round2(printed ? gross - printed.amount : gross * tier.pct / 100), date: tier.date, derived: true, from };
        } else {
          discount = { available: null, amount: null, date: null, derived: false };   // paid in March or later: none
        }
      } else {
        discount = { available: null, amount: null, date: null, derived: false };
        review.push('FL paid bill with no paid date — discount unknown');
      }
    }
  }
  if (paid && billed == null) review.push('Paid installment — its printed amount was not found; enter Billed by hand');

  const printedPaid = num(i.amountPaid);
  const paidAmount = !paid ? null
    : printedPaid != null ? printedPaid
    : (fl && billed != null && discount.amount != null && discount.amount > 0) ? round2(billed - discount.amount)
    : null;

  return {
    paid, detected,
    billed: billed == null ? null : round2(billed),
    paidAt: paid ? (i.paidDate || null) : null,
    paidAmount,
    discount,
    discountSchedule: tiers.length ? tiers : null,
    needsReview: review.length > 0,
    reviewNote: review.length ? review.join(' · ') : null,
  };
}
